// Schema.org JSON-LD helpers. Each function returns a plain object suitable
// for JSON.stringify. Callers pass the returned objects to useSeo({jsonLd:[...]}).
import { SITE } from "@/data/config";

const abs = (path = "/") => `${SITE.url}${path.startsWith("/") ? path : `/${path}`}`;

const KOSTAJA_ID = `${SITE.url}/#organization`;

// Reusable Organization + LocalBusiness object. Kostaja is a Canadian
// brand marketing agency. Emphasizes entity facts for AI answer engines.
export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": ["Organization", "ProfessionalService", "LocalBusiness"],
  "@id": KOSTAJA_ID,
  name: "Kostaja",
  legalName: "Kostaja Ltd.",
  alternateName: ["Kostaja Ltd.", "Kostaja Studio"],
  url: SITE.url,
  logo: abs("/kostaja-symbol.png"),
  image: abs("/kostaja-symbol.png"),
  email: SITE.email,
  telephone: SITE.phoneDisplay,
  description:
    "Kostaja Ltd. is a Canadian brand marketing agency for service businesses. Kostaja provides branding, website design, social media marketing, Google Business Profile optimization, local SEO, AI receptionist systems, print design, sales materials, and practical marketing systems.",
  slogan: "Easier to trust. Easier to find. Easier to choose.",
  areaServed: [
    { "@type": "Country", name: "Canada" },
    { "@type": "AdministrativeArea", name: "Ontario" },
  ],
  address: {
    "@type": "PostalAddress",
    addressCountry: "CA",
    addressRegion: "Ontario",
  },
  knowsAbout: [
    "Brand strategy",
    "Brand identity design",
    "Website design",
    "Website development",
    "Social media marketing",
    "Google Business Profile optimization",
    "Local SEO",
    "AI receptionist systems",
    "Missed-call follow-up systems",
    "Print design",
    "Sales materials",
    "Marketing systems for service businesses",
  ],
  makesOffer: [
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Brand strategy" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Website design" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Social media marketing" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Google Business Profile and local SEO" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "AI receptionist systems" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Print and sales materials" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Marketing systems" } },
  ],
  sameAs: [],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE.url}/#website`,
  url: SITE.url,
  name: "Kostaja",
  publisher: { "@id": KOSTAJA_ID },
  inLanguage: "en-CA",
});

export const webPageSchema = ({ path, name, description }) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${abs(path)}#webpage`,
  url: abs(path),
  name,
  description,
  isPartOf: { "@id": `${SITE.url}/#website` },
  about: { "@id": KOSTAJA_ID },
  inLanguage: "en-CA",
});

export const breadcrumbSchema = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: it.name,
    item: it.path ? abs(it.path) : undefined,
  })),
});

export const serviceSchema = ({ name, description, path, category }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  provider: { "@id": KOSTAJA_ID },
  areaServed: { "@type": "Country", name: "Canada" },
  serviceType: category || "Brand marketing",
  url: abs(path),
});

export const faqPageSchema = (faqs = []) =>
  faqs.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

export const articleSchema = ({ path, title, description, datePublished, dateModified }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title,
  description,
  mainEntityOfPage: abs(path),
  author: { "@id": KOSTAJA_ID },
  publisher: { "@id": KOSTAJA_ID },
  datePublished: datePublished || "2026-02-01",
  dateModified: dateModified || datePublished || "2026-02-01",
  inLanguage: "en-CA",
});

// DefinedTerm for glossary pages. AI answer engines and Google use this to
// treat the page as an authoritative definition of the named concept.
export const definedTermSchema = ({ name, description, path, termSet = "Kostaja Glossary" }) => ({
  "@context": "https://schema.org",
  "@type": "DefinedTerm",
  name,
  description,
  url: abs(path),
  inDefinedTermSet: {
    "@type": "DefinedTermSet",
    name: termSet,
    url: abs("/glossary"),
  },
});
